import React, { useEffect, useState } from "react";
import {
  MdOutlineGroups,
  MdOutlineEvent,
  MdOutlineCampaign,
  MdOutlinePhotoLibrary,
  MdOutlineShare,
} from "react-icons/md";
import { LiaDonateSolid } from "react-icons/lia";
import { GiExpense } from "react-icons/gi";
import moment from "moment";
import { toast } from "react-toastify";
import styles from "./Dashboard.module.scss";
import {
  getMembers,
  getEvents,
  getPosts,
  getGalleryEntry,
  getSumFromCollection,
  getDonationEntries,
} from "../../utils/network/Crud";
import { buildDaySummaryPDF, sharePDF } from "../../utils/pdfReport";
import useTheme from "../../utils/useTheme";

const Overview = () => {
  const [stats, setStats] = useState({
    members: 0,
    events: 0,
    posts: 0,
    gallery: 0,
    donations: 0,
    expenses: 0,
  });
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const { currentYear } = useTheme();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [members, events, posts, gallery, donations, expenses] =
        await Promise.all([
          getMembers(),
          getEvents(),
          getPosts(),
          getGalleryEntry(),
          getSumFromCollection("donation", "amount"),
          getSumFromCollection("expense", "amount"),
        ]);
      setStats({
        members: members?.data?.length || 0,
        events: events?.data?.length || 0,
        posts: posts?.data?.length || 0,
        gallery: gallery?.data?.images?.length || 0,
        donations: donations || 0,
        expenses: expenses || 0,
      });
      setLoading(false);
    };
    load();
  }, []);

  const cards = [
    { label: "Members", value: stats.members, icon: <MdOutlineGroups /> },
    { label: "Upcoming/Past Events", value: stats.events, icon: <MdOutlineEvent /> },
    { label: "Announcements", value: stats.posts, icon: <MdOutlineCampaign /> },
    { label: "Gallery Photos", value: stats.gallery, icon: <MdOutlinePhotoLibrary /> },
    {
      label: "Total Donations",
      value: `₹ ${stats.donations.toLocaleString()}`,
      icon: <LiaDonateSolid />,
    },
    {
      label: "Total Expenses",
      value: `₹ ${stats.expenses.toLocaleString()}`,
      icon: <GiExpense />,
    },
  ];

  const handleShareSummary = async () => {
    setSharing(true);
    try {
      const today = moment().format("YYYY-MM-DD");
      const [donationTotal, expenseTotal, todayDonations, todayExpenses] =
        await Promise.all([
          getSumFromCollection("donation", "amount", currentYear),
          getSumFromCollection("expense", "amount", currentYear),
          getDonationEntries("donation", "", true, today, currentYear),
          getDonationEntries("expense", "", true, today, currentYear),
        ]);

      const doc = buildDaySummaryPDF({
        year: currentYear,
        donationTotal,
        expenseTotal,
        todayDonations: todayDonations?.data || [],
        todayExpenses: todayExpenses?.data || [],
      });

      const shareText = `Navdurga Utsav Samiti — Day-End Summary (${moment().format(
        "DD MMM YYYY"
      )})\nTotal Donations (FY ${currentYear}): Rs. ${donationTotal.toLocaleString()}\nTotal Expenses (FY ${currentYear}): Rs. ${expenseTotal.toLocaleString()}\nNet Balance: Rs. ${(
        donationTotal - expenseTotal
      ).toLocaleString()} 🙏`;

      await sharePDF(
        doc,
        `Day-End-Summary_${moment().format("YYYY-MM-DD")}.pdf`,
        shareText
      );
    } catch (error) {
      console.error("Error sharing day summary:", error);
      toast.error("Something went wrong generating the summary!", {
        autoClose: 1200,
      });
    } finally {
      setSharing(false);
    }
  };

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h2>Overview</h2>
          <p>Live snapshot of your samiti's data.</p>
        </div>
        <button
          className={styles.primaryBtn}
          onClick={handleShareSummary}
          disabled={sharing}
        >
          <MdOutlineShare />
          {sharing ? "Preparing…" : "Share Day-End Summary"}
        </button>
      </div>
      <div className={styles.statsGrid}>
        {cards.map((card) => (
          <div className={styles.statCard} key={card.label}>
            <div className={styles.statIcon}>{card.icon}</div>
            <div>
              <h3>{loading ? "…" : card.value}</h3>
              <p>{card.label}</p>
            </div>
          </div>
        ))}
      </div>
      <div className={styles.panel}>
        <h3 style={{ marginTop: 0, color: "#f0b23c" }}>Quick Guide</h3>
        <p style={{ color: "#b6acc9", fontSize: 13, lineHeight: 1.7, margin: 0 }}>
          Use the sidebar to add members, publish events &amp; announcements,
          manage the gallery, control YouTube live streaming, and toggle which
          sections appear on the public site from <b>Site Settings</b>.
        </p>
      </div>
    </div>
  );
};

export default Overview;
